import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import {
  UsersApi,
  RequestStrategyEnum,
  type AddUserCardsRequest,
  type DeleteUserCardsRequest,
  type UsersRequest,
  type UserResponse,
  type WithSignal,
} from '@shared/api';

const getUsersEffect = createEffect(({ signal, ...request }: WithSignal<UsersRequest>) => UsersApi.getUsers(request, signal));
const addUsersInCardEffect = createEffect((request: AddUserCardsRequest) => UsersApi.addUserCards(request));
const deleteUserInCardEffect = createEffect((request: DeleteUserCardsRequest) => UsersApi.deleteUserCards(request));
const clearEvent = createEvent();

const $dataStore = createStore<UserResponse[]>([])
  .on(getUsersEffect.done, (current, data) => data.result.data || current)
  .on(addUsersInCardEffect.done, (current, data) => (data.result.data ? [...current, data.result.data] : current))
  .on(deleteUserInCardEffect.done, (current, data) => current.filter((user) => !data.params.userIds.includes(user.id)))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUsersEffect.pending, (_, data) => data)
  .on(getUsersEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getUsersEffect.done, () => false)
  .reset(clearEvent);
const $actionLoadingStore = createStore<boolean>(false)
  .on(addUsersInCardEffect.pending, (_, data) => data)
  .on(addUsersInCardEffect.fail, () => false)
  .on(addUsersInCardEffect.done, () => false)
  .on(deleteUserInCardEffect.pending, (_, data) => data)
  .on(deleteUserInCardEffect.fail, () => false)
  .on(deleteUserInCardEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUsersEffect, () => null)
  .on(getUsersEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getUsersEffect.done, () => null)
  .reset(clearEvent);
const $actionErrorStore = createStore<string | null>(null)
  .on(addUsersInCardEffect, () => null)
  .on(
    addUsersInCardEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while adding User. Please try again.').message,
  )
  .on(addUsersInCardEffect.done, () => null)
  .on(deleteUserInCardEffect, () => null)
  .on(
    deleteUserInCardEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while deleting User. Please try again.').message,
  )
  .on(deleteUserInCardEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUsersApi = ApiUtils.wrapperApi(getUsersEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getUsersApi.cancel());

export const $cardUsersStore = {
  events: {
    getUsers: getUsersApi.trigger,
    addUsersInCard: addUsersInCardEffect,
    deleteUserInCard: deleteUserInCardEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
